"use client";

import { getAuthToken, removeAuthToken } from "@/lib/auth";
import { AccountCircle, Menu as MenuIcon } from "@mui/icons-material";
import {
  AppBar,
  IconButton,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
} from "@mui/material";
import Image from "next/image";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useState } from "react";

export default function Navbar() {
  const router = useRouter();
  const pathname = usePathname();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Check if current page is blog-related (where avatar should show)
  const isBlogPage =
    pathname === "/blog" ||
    pathname.startsWith("/blog/") ||
    pathname === "/create" ||
    pathname.startsWith("/edit");

  useEffect(() => {
    setIsLoggedIn(!!getAuthToken());

    const handleAuthChange = () => setIsLoggedIn(!!getAuthToken());
    window.addEventListener("authChange", handleAuthChange);

    return () => window.removeEventListener("authChange", handleAuthChange);
  }, []);

  const handleHomeClick = () => {
    router.push("/");
    setMobileOpen(false);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleCreateClick = () => {
    handleMenuClose();
    router.push("/create");
  };

  const handleNavClick = (path) => {
    if (pathname === path) {
      handleDrawerToggle(); // Just close the mobile menu if open
      return;
    }
    router.push(path);
    handleDrawerToggle(); // Close menu after navigating
  };

  const handleLogout = () => {
    removeAuthToken();
    setIsLoggedIn(false);
    handleMenuClose();
    router.push("/");
  };

  // Nav links configuration - Portal separated at the end
  const navLinks = [
    { label: "About", href: "/about" },
    { label: "Academics", href: "/academics" },
    { label: "Admissions", href: "/admissions" },
    { label: "Give", href: "/support" },
    { label: "Contact", href: "/contact" },
    { label: "Blog", href: "/blog" },
  ];

  // Portal is separate for visual distinction
  const portalLink = { label: "Portal", href: "/portal" };

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: "center", pt: 7 }}>
      <Box
        onClick={(e) => {
          e.stopPropagation();
          handleHomeClick();
        }}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          cursor: "pointer",
          mb: 2,
          WebkitTapHighlightColor: "transparent",
        }}
      ></Box>
      <List>
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => handleNavClick("/")}
            sx={{
              textAlign: "center",
              color: pathname === "/" ? "secondary.main" : "text.primary",
              backgroundColor:
                pathname === "/" ? "primary.main" : "transparent",
              borderRadius: 1,
              mx: 2,
              "&:hover": {
                backgroundColor:
                  pathname === "/"
                    ? "primary.main"
                    : "rgba(255, 230, 140, 0.15)",
              },
            }}
          >
            <ListItemText
              primary="Home"
              primaryTypographyProps={{
                fontWeight: 500,
                fontSize: "1.1rem",
              }}
            />
          </ListItemButton>
        </ListItem>
        {navLinks.map((link) => (
          <ListItem key={link.href} disablePadding>
            <ListItemButton
              onClick={() => handleNavClick(link.href)}
              sx={{
                textAlign: "center",
                color:
                  pathname === link.href ? "secondary.main" : "text.primary",
                backgroundColor:
                  pathname === link.href ? "primary.main" : "transparent",
                borderRadius: 1,
                mx: 2,
                "&:hover": {
                  backgroundColor:
                    pathname === link.href
                      ? "primary.main"
                      : "rgba(255, 230, 140, 0.15)",
                },
              }}
            >
              <ListItemText
                primary={link.label}
                primaryTypographyProps={{
                  fontWeight: 500,
                  fontSize: "1.1rem",
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}

        {/* Divider before Portal */}
        <Box
          sx={{
            mx: 4,
            my: 1,
            borderTop: "1px solid",
            borderColor: "divider",
            opacity: 0.5,
          }}
        />

        {/* Portal - separated from other links */}
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => handleNavClick(portalLink.href)}
            sx={{
              textAlign: "center",
              color:
                pathname === portalLink.href
                  ? "secondary.main"
                  : "text.primary",
              backgroundColor:
                pathname === portalLink.href ? "primary.main" : "transparent",
              borderRadius: 1,
              mx: 2,
              "&:hover": {
                backgroundColor:
                  pathname === portalLink.href
                    ? "primary.main"
                    : "rgba(255, 230, 140, 0.15)",
              },
            }}
          >
            <ListItemText
              primary={portalLink.label}
              primaryTypographyProps={{
                fontWeight: 500,
                fontSize: "1.1rem",
              }}
            />
          </ListItemButton>
        </ListItem>
        {/* Mobile Login/Logout if needed - currently sticking to main navigation */}
      </List>
    </Box>
  );

  return (
    <AppBar
      position="fixed"
      sx={{ backgroundColor: "primary.main", zIndex: 1300 }}
    >
      <Toolbar sx={{ justifyContent: "space-between", py: 1 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
            gap: 2,
            WebkitTapHighlightColor: "transparent",
          }}
          onClick={handleHomeClick}
        >
          <Image
            src="/apple-touch-icon.png"
            alt="Sun Hive Logo"
            width={48}
            height={48}
            style={{ objectFit: "contain" }}
          />
          <Typography
            variant="h6"
            component="div"
            sx={{
              color: "text.inverse",
              fontWeight: 600,
              lineHeight: 1.2,
            }}
          >
            Sun Hive
            <br />
            Microschool
          </Typography>
        </Box>

        {/* Desktop Navigation Links */}
        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            alignItems: "center",
            gap: "1.5rem",
          }}
        >
          {navLinks.map((link) => (
            <Typography
              key={link.href}
              sx={{
                color:
                  pathname === link.href ? "secondary.main" : "text.inverse",
                textDecoration: "none",
                fontWeight: 500,
                cursor: "pointer",
                "&:hover": { color: "secondary.main" },
              }}
              onClick={() => handleNavClick(link.href)}
            >
              {link.label}
            </Typography>
          ))}

          {/* Separator */}
          <Typography
            sx={{
              color: "text.inverse",
              opacity: 0.4,
              userSelect: "none",
            }}
          >
            |
          </Typography>

          {/* Portal - separated from other links */}
          <Typography
            sx={{
              color:
                pathname === portalLink.href
                  ? "secondary.main"
                  : "text.inverse",
              textDecoration: "none",
              fontWeight: 500,
              cursor: "pointer",
              "&:hover": { color: "secondary.main" },
            }}
            onClick={() => handleNavClick(portalLink.href)}
          >
            {portalLink.label}
          </Typography>

          {/* Only show avatar on blog-related pages when logged in */}
          {isLoggedIn && isBlogPage && (
            <>
              <IconButton
                onClick={handleMenuOpen}
                sx={{ color: "text.inverse" }}
                aria-label="User menu"
              >
                <AccountCircle sx={{ fontSize: 32 }} />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                slotProps={{
                  paper: {
                    sx: {
                      backgroundColor: "background.paper",
                      border: "1px solid",
                      borderColor: "background.hover",
                      color: "text.primary",
                    },
                  },
                }}
              >
                <MenuItem
                  onClick={handleCreateClick}
                  sx={{ "&:hover": { backgroundColor: "background.hover" } }}
                >
                  Create
                </MenuItem>
                <MenuItem
                  onClick={handleLogout}
                  sx={{
                    color: "text.secondary",
                    "&:hover": { backgroundColor: "background.hover" },
                  }}
                >
                  Logout
                </MenuItem>
              </Menu>
            </>
          )}
        </Box>

        {/* Mobile Hamburger Menu Icon */}
        <IconButton
          color="inherit"
          aria-label="open drawer"
          edge="start"
          onClick={handleDrawerToggle}
          sx={{ display: { md: "none" }, color: "text.inverse" }}
        >
          <MenuIcon />
        </IconButton>
      </Toolbar>

      {/* Mobile Navigation Drawer */}
      <Drawer
        variant="temporary"
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": { boxSizing: "border-box", width: 240 },
        }}
      >
        {drawer}
      </Drawer>
    </AppBar>
  );
}
